import { Badge, Box } from '@chakra-ui/react';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { IoCheckmarkDone } from 'react-icons/io5';
import { useNavigate } from 'react-router';
import NotificationCheck from '@/assets/icons/NotificationCheck';
import NotificationRead from '@/assets/icons/NotificationRead';
import NotificationUnread from '@/assets/icons/NotificationUnread';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import PendingNotification from '@/components/Molecules/NotificationsList/PendingNotification';
import Show from '@/components/Molecules/Show';
import NotificationsListSkeleton from '@/components/Templates/Notifications/NotificationsListSkeleton';
import { useColorModeValue } from '@/components/ui/color-mode-utils';
import { useNotificationContext } from '@/components/ui/notification-provider';
import { colors } from '@/components/ui/theme';
import {
	useGetNotifications,
	useGetPendingNotifications,
	useMarkAllNotificationsAsRead,
	useRemovePendingNotifications,
} from '@/hooks/service-hooks/notifications';
import { MenuPrimitives as PMMenu } from '@/types/MenuPrimitives.types';

interface NotificationsListProps {
	trigger: React.ReactNode;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
}

const NotificationsList: React.FC<NotificationsListProps> = ({
	trigger,
	isOpen,
	onOpenChange,
}) => {
	const { isEnabled: notificationsEnabled, userId } = useNotificationContext();
	const triggerRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();

	const menuBg = useColorModeValue('white', colors.tabelCellDark);
	const textColor = useColorModeValue('gray.800', 'gray.100');

	// If notifications are disabled, just render the trigger without functionality
	if (!notificationsEnabled) {
		return;
	}

	const handleItemClick = (notificationId: string) => {
		if (notificationId) {
			navigate(`/notifications/${notificationId}`);
		}
		onOpenChange(false);
	};

	const handleSeeAll = () => {
		onOpenChange(false);
		navigate('/notifications');
	};

	const hiddenRoutes = ['/login', '/choose-platform'];
	const isNotificationsVisible = window?.location
		? !hiddenRoutes.includes(window.location.pathname) : false

	const { data: notifications, isLoading } = useGetNotifications({
		userId: userId || null,
		page: 1,
		limit: 10,
		all: true,
		date: '',
		enabled: !!userId,
	});
	const { data: pendingNotifications } = useGetPendingNotifications(
		userId,
		1,
		5,
		!!userId && isNotificationsVisible,
	);
	const { mutate: markAllAsRead, isLoading: isMarkingAsRead } =
		useMarkAllNotificationsAsRead(
			() => {
				toast.success('All notifications marked as read');
			},
			(error) => {
				toast.error('Failed to mark all as read');
			},
		);
	const { mutate: removePending } = useRemovePendingNotifications();

	const handleMarkAllAsRead = () => {
		if (userId) {
			markAllAsRead(userId);
		}
	};

	// Show toast for all pending notifications
	useEffect(() => {
		if (
			pendingNotifications &&
			pendingNotifications?.notifications?.length > 0 &&
			userId
		) {
			const reversedNotifications =
				pendingNotifications.notifications.reverse();
			const totalNotifications = reversedNotifications.length;

			reversedNotifications.forEach((item, index) => {
				// Add 1 second delay between each toast
				setTimeout(() => {
					toast.custom(
						(t) => (
							<PendingNotification
								onClick={handleItemClick}
								item={item}
								toastId={t.id}
								mt={3}
							/>
						),
						{
							duration: 10000,
							position: 'top-right',
						},
					);

					// After the last toast is shown, remove pending notifications from backend
					if (index === totalNotifications - 1) {
						removePending(userId);
					}
				}, index * 1000); // 1000ms = 1 second
			});
		}
		// Cleanup: dismiss all toasts if component unmounts or dependencies change
		return () => {
			toast.dismissAll();
		};
	}, [pendingNotifications, userId]);

	return (
		<Box ref={triggerRef} cursor="pointer" position="relative">
			<PMMenu.Root
				open={isOpen}
				onOpenChange={(d: { open: boolean }) => onOpenChange(d.open)}
				positioning={{ placement: 'bottom-end' }}
			>
				<PMMenu.Trigger
					_focus={{ boxShadow: 'none', outline: 'none' }}
					_focusVisible={{ boxShadow: 'none', outline: 'none' }}
					border="none"
				>
					{trigger}
				</PMMenu.Trigger>
				<Show
					when={
						!!notifications?.totalUnreadItems &&
						notifications?.totalUnreadItems > 0
					}
				>
					<Badge
						position="absolute"
						top="-2px"
						right="-2px"
						borderRadius="full"
						bg="red.500"
						color="white"
						fontSize="10px"
						minW="18px"
						h="18px"
						display="flex"
						alignItems="center"
						justifyContent="center"
						fontWeight="bold"
						px={1}
						zIndex={1}
					>
						{notifications?.totalUnreadItems &&
						notifications?.totalUnreadItems > 9
							? '9+'
							: notifications?.totalUnreadItems}
					</Badge>
				</Show>
				<PMMenu.Positioner>
					<PMMenu.Content
						bg={menuBg}
						borderRadius="lg"
						boxShadow="xl"
						p={0}
						width={400}
						minHeight="96"
						maxHeight="96"
						zIndex="popover"
						color={textColor}
						display="flex"
						flexDirection="column"
						transition="height 0.3s ease-in-out"
						overflow="hidden"
					>
						<Box
							overflowY="auto"
							display="flex"
							flexDirection="column"
							flex={1}
							gap={2}
							p={2}
						>
							<Show when={isLoading}>
								<NotificationsListSkeleton rows={10} />
							</Show>
							<Show when={!isLoading && notifications?.items?.length === 0}>
								<Box
									p={2}
									textAlign="center"
									fontSize="sm"
									color="gray.500"
									fontWeight="bold"
								>
									No notifications
								</Box>
							</Show>

							<Show
								when={
									!isLoading &&
									!!notifications?.items &&
									notifications?.items?.length > 0
								}
							>
								{notifications?.items &&
									notifications?.items?.length > 0 &&
									notifications?.items?.map((item) => (
										<Box
											key={item.notificationId}
											p={1}
											pr={2}
											display="flex"
											alignItems="center"
											justifyContent="space-between"
											cursor="pointer"
											bg={
												!item.isRead
													? { base: 'gray.100', _dark: 'gray.700' }
													: { base: 'white', _dark: 'gray.800' }
											}
											color={
												!item.isRead
													? { base: textColor, _dark: 'white' }
													: textColor
											}
											_hover={{
												bg: 'brand.solidGradient',
												color: 'white',
												cursor: 'pointer',
												'& > div > div': { color: 'white' },
												'& svg': { color: 'white' },
											}}
											onClick={() => handleItemClick(item.notificationId)}
											borderRadius="lg"
											gap={2}
											minH={16}
											shadow={{
												_dark: !item.isRead ? 'xs' : 'sm',
												_light: 'sm',
											}}
										>
											<Box display="flex" alignItems="center" flex={1}>
												<Box display="flex" alignItems="center">
													<NotificationCheck height={30} width={35} />
												</Box>
												<Box
													display="flex"
													flexDirection="column"
													gap={0.5}
													maxW="300px"
												>
													<Box
														fontSize="xs"
														fontWeight={!item.isRead ? 600 : 400}
														whiteSpace="normal"
														wordBreak="break-word"
													>
														{item.body.length > 90
															? `${item.body.slice(0, 90)}...`
															: item.body}
													</Box>
												</Box>
											</Box>
											<Box
												display="flex"
												flexDirection="column"
												alignItems="flex-end"
												justifyContent="center"
												gap={1}
											>
												<Box display="flex" alignItems="center">
													<Show when={!item.isRead}>
														<NotificationRead height={30} width={35} />
													</Show>
													<Show when={item.isRead}>
														<NotificationUnread height={30} width={35} />
													</Show>
												</Box>
												<Box
													fontSize="2xs"
													fontWeight="400"
													opacity={0.7}
													textAlign="right"
												>
													{dayjs(item?.createdOn)
														.tz('America/New_York')
														.format('MM/DD/YYYY hh:mm A')}
												</Box>
											</Box>
										</Box>
									))}
							</Show>
						</Box>
						<Box
							p={2}
							display="flex"
							gap={1}
							justifyContent="space-between"
							shadow="sm"
						>
							<FaceliftButton
								_hover={{ bg: { base: 'gray.100', _dark: 'gray.700' } }}
								borderRadius="sm"
								bg="transparent"
								color={textColor}
								onClick={handleSeeAll}
								loading={isLoading}
								cursor="pointer"
							>
								See All
							</FaceliftButton>
							<FaceliftButton
								_hover={{ bg: { base: 'gray.100', _dark: 'gray.700' } }}
								borderRadius="sm"
								bg="transparent"
								color={textColor}
								onClick={handleMarkAllAsRead}
								loading={isMarkingAsRead}
								cursor="pointer"
							>
								<IoCheckmarkDone /> Mark all as read
							</FaceliftButton>
						</Box>
					</PMMenu.Content>
				</PMMenu.Positioner>
			</PMMenu.Root>
		</Box>
	);
};

export default NotificationsList;
